import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for Undo/Redo functionality
 * Tracks changes and allows reverting/restoring them
 */
const useUndoRedo = (onUndo, onRedo, maxHistory = 50) => {
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Record an action that can be undone
  const recordAction = useCallback((action) => {
    // action should have: { type, description, undo: async () => {}, redo: async () => {} }
    setUndoStack(prev => {
      const newStack = [...prev, action];
      // Limit stack size
      if (newStack.length > maxHistory) {
        newStack.shift();
      }
      return newStack;
    });
    // Clear redo stack when new action is recorded
    setRedoStack([]);
  }, [maxHistory]);

  // Undo the last action
  const undo = useCallback(async () => {
    if (undoStack.length === 0) {
      toast.info('Nichts zum Rückgängig machen');
      return false;
    }

    const action = undoStack[undoStack.length - 1];
    
    try {
      await action.undo();
      
      // Move action to redo stack
      setUndoStack(prev => prev.slice(0, -1));
      setRedoStack(prev => [...prev, action]);
      
      toast.success(`Rückgängig: ${action.description}`);
      
      if (onUndo) onUndo(action);
      return true;
    } catch (error) {
      toast.error('Fehler beim Rückgängig machen');
      console.error('Undo error:', error);
      return false;
    }
  }, [undoStack, onUndo]);

  // Redo the last undone action
  const redo = useCallback(async () => {
    if (redoStack.length === 0) {
      toast.info('Nichts zum Wiederherstellen');
      return false;
    }

    const action = redoStack[redoStack.length - 1];
    
    try {
      await action.redo();
      
      // Move action back to undo stack
      setRedoStack(prev => prev.slice(0, -1));
      setUndoStack(prev => [...prev, action]);
      
      toast.success(`Wiederhergestellt: ${action.description}`);
      
      if (onRedo) onRedo(action);
      return true;
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
      console.error('Redo error:', error);
      return false;
    }
  }, [redoStack, onRedo]);

  // Clear all history
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if focus is in an input/textarea
      const activeElement = document.activeElement;
      const isEditing = activeElement?.tagName === 'INPUT' || 
                       activeElement?.tagName === 'TEXTAREA' ||
                       activeElement?.isContentEditable;
      
      if (isEditing) return;

      // Ctrl+Z or Cmd+Z for Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      
      // Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y for Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    recordAction,
    undo,
    redo,
    clearHistory,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoCount: undoStack.length,
    redoCount: redoStack.length,
    lastAction: undoStack[undoStack.length - 1] || null
  };
};

export default useUndoRedo;
